import { useMemo } from 'react';
import NumberInput from '@/components/NumberInput';
import OrderItemPromotionAlert from '@/modules/order/components/OrderItemPromotionAlert';
import { Order } from '@/modules/order/models/Order';
import { calculateConfirmedOrderItemPrice } from '@/modules/order/services/calculateConfirmedOrderItemPrice';
import { formatPrice } from '@/utils/currency';
import { getTaxLabels } from '@/utils/tax';

interface OrderItemQuantities {
  [orderItemId: string]: number;
}

interface OrderItemEditTableProps {
  order: Order;
  confirmedQuantities?: OrderItemQuantities;
  onChangeConfirmedQuantity?: (quantity: number, orderItemId: string) => void;
  countryCode?: string;
  showVat?: boolean;
  hidePriceData?: boolean;
}

const OrderItemEditTable = ({
  order,
  confirmedQuantities = {},
  onChangeConfirmedQuantity = () => {},
  countryCode,
  showVat = true,
  hidePriceData = false,
}: OrderItemEditTableProps) => {
  const taxLabels = getTaxLabels(countryCode);
  const { subtotal, grandTotal } = useMemo(() => {
    let subtotal = 0;
    let totalVatAmount = 0;

    order.items.forEach((item) => {
      const quantity = confirmedQuantities[item.id] ?? item.confirmedQuantity ?? item.quantity;
      const lineItemAmount =
        calculateConfirmedOrderItemPrice(item, quantity)?.lineItemPrice.amount ?? 0;
      const vatRate = item.vatRate ?? 0;

      subtotal += lineItemAmount;
      totalVatAmount += lineItemAmount * vatRate;
    });

    const grandTotal = subtotal + totalVatAmount;

    return {
      subtotal,
      grandTotal,
    };
  }, [order.items, confirmedQuantities]);

  const totalColumns = 5 + (!hidePriceData && showVat ? 1 : 0) + (!hidePriceData ? 1 : 0);

  return (
    <div className="shadow-md rounded-lg">
      <div className="relative overflow-x-auto border rounded-t-lg">
        <table className="min-w-full" data-testid="order-items-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-300 text-gray-500 font-medium text-sm">
              <th className="px-6 py-3 pr-3 h-12 w-20 text-left">#</th>
              <th className="px-6 py-3 text-left w-96">SKU</th>
              <th className="px-6 py-3 text-left w-40">SKU Code</th>
              <th className="px-6 py-3 text-left">Ordered Quantity</th>
              <th className="px-6 py-3 text-left">Confirmed Quantity</th>
              {!hidePriceData && (
                <>
                  {showVat && <th className="px-6 py-3 text-left">{taxLabels.rate}</th>}
                  <th className="px-3 py-3 pr-6 text-right w-40">
                    {showVat ? taxLabels.subtotalExcluding : 'Subtotal'}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {order.items.map((orderItem) => {
              const quantity =
                confirmedQuantities[orderItem.id] ??
                orderItem.confirmedQuantity ??
                orderItem.quantity;
              const lineItemAmount =
                calculateConfirmedOrderItemPrice(orderItem, quantity)?.lineItemPrice.amount ?? 0;

              return (
                <tr
                  key={orderItem.id}
                  className="text-sm lg:text-base"
                  data-testid="order-item-row"
                  data-sku-code={orderItem.skuCode}
                  data-sku-name={orderItem.skuName}
                >
                  <td className="px-6 py-4">{orderItem.lineNumber}</td>
                  <td className="px-6 py-4" data-testid="order-item-name">
                    {orderItem.skuName}

                    {(orderItem.promotions?.length || 0) > 0 && (
                      <div className="mt-1 space-y-1">
                        <OrderItemPromotionAlert item={orderItem} />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" data-testid="order-item-sku-code">
                    {orderItem.skuCode}
                  </td>
                  <td className="px-6 py-4" data-testid="order-item-ordered-quantity">
                    {orderItem.quantity}
                  </td>
                  <td className="px-3 py-1">
                    <NumberInput
                      data-testid="order-item-confirmed-quantity-input"
                      value={quantity}
                      min={0}
                      max={1_000_000}
                      onChange={(quantity: number) =>
                        onChangeConfirmedQuantity(quantity, orderItem.id)
                      }
                    />
                  </td>
                  {!hidePriceData && (
                    <>
                      {showVat && <td className="px-6 py-4">{(orderItem.vatRate ?? 0) * 100}%</td>}
                      <td className="px-3 py-4 pr-6 text-right">
                        {formatPrice(lineItemAmount, orderItem.lineItemPrice?.currencyCode)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          {!hidePriceData && (
            <tbody className="border-t border-gray-300">
              <tr>
                <td
                  colSpan={totalColumns - 1}
                  className="text-right px-3 pr-1 py-1 pt-6 font-semibold text-sm"
                >
                  {taxLabels.subtotalExcluding}
                </td>
                <td className="px-3 pt-6 pr-6 text-right">
                  {formatPrice(subtotal, order.subtotal?.currencyCode)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan={totalColumns - 1}
                  className={`text-right px-3 pr-1 py-1 font-semibold text-sm ${
                    !showVat ? 'pb-6' : ''
                  }`}
                >
                  {taxLabels.totalExcluding}
                </td>
                <td className={`px-3 py-1 pr-6 text-right ${!showVat ? 'pb-6' : ''}`}>
                  {formatPrice(subtotal, order.subtotal?.currencyCode)}
                </td>
              </tr>
              {showVat && (
                <tr>
                  <td
                    colSpan={totalColumns - 1}
                    className="text-right px-3 pr-1 py-1 pb-6 font-semibold text-sm"
                  >
                    {taxLabels.grandTotalIncluding}
                  </td>
                  <td className="px-3 py-1 pb-6 pr-6 text-right">
                    {formatPrice(grandTotal, order.grandTotal?.currencyCode)}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default OrderItemEditTable;
